import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { getProfileUrl } from '../../utils/guideDataAdapters';
import Avatar from '../common/Avatar';

/**
 * GuideCard Component
 * Displays guide profile summary with stats and bio
 *
 * Data format (use adaptProfileForGuideCard from utils/guideDataAdapters.js):
 * {
 *   id: UUID,
 *   slug: string (optional),
 *   name: string,
 *   bio: string,
 *   avatar: string (URL),
 *   stats: { reviews: number, rating: number, trips: number }
 * }
 */
export default function GuideCard({
  guide = {
    id: null,
    slug: null,
    name: 'Guide',
    bio: '',
    avatar: '/api/placeholder/120/120',
    stats: {
      reviews: 0,
      rating: 0,
      trips: 0
    }
  }
}) {
  const theme = useTheme();
  const { id, slug, name, bio, avatar, stats } = guide;

  // Use slug-based URL if available, fallback to ID
  const guideProfileUrl = getProfileUrl(slug, id);

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
      <h2 className="text-xl font-bold mb-4">
        Meet Your Guide:{' '}
        <Link
          to={guideProfileUrl}
          className={`${theme.textPrimary} ${theme.textHover} hover:underline transition`}
        >
          {name}
        </Link>
      </h2>
      <div className="flex items-start gap-6">
        <Avatar
          src={avatar}
          name={name}
          size="2xl"
          className="flex-shrink-0"
        />
        <div className="flex-1">
          <p className="text-gray-700 mb-4">{bio}</p>
          <div className="flex flex-wrap gap-4 text-sm">
            {stats.reviews > 0 && (
              <div>
                <div className="font-semibold text-gray-900">{stats.reviews}</div>
                <div className="text-gray-600">Reviews</div>
              </div>
            )}
            {stats.rating > 0 && (
              <div>
                <div className="font-semibold text-gray-900">{stats.rating}★</div>
                <div className="text-gray-600">Rating</div>
              </div>
            )}
            {stats.trips > 0 && (
              <div>
                <div className="font-semibold text-gray-900">{stats.trips}</div>
                <div className="text-gray-600">Trips</div>
              </div>
            )}
          </div>
          <Link
            to={guideProfileUrl}
            className={`inline-flex items-center gap-1 text-sm ${theme.textPrimary} ${theme.textHover} mt-3 transition`}
          >
            <span>More trips by {name.split(' ')[0]}</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
